/**
 * Shared "Franchise" two-line cell renderer.
 *
 * Used by every platform report (exception-based via PLATFORM_CONTEXT_COLUMNS
 * and analysis reports via BaseReportPage's effectiveColumns auto-collapse).
 * Replaces the historical FP Code / Franchise / FP Status trio on the browser
 * view; exports keep the three columns separate via the backend COLUMNS path.
 *
 * Layout:
 *   <bold>{franchise name}</bold>
 *   [CC] {fp_code} - {fp_status}
 *
 * The country chip uses the central ``utils/countryChips.ts`` palette so
 * the cell stays visually aligned with the admin FP list, COGS Tracker,
 * FP directory, and FP switcher.
 *
 * The top line is wrapped in a Router Link so right-click / ctrl-click open
 * the inspector in a new tab.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { deriveCountryCode, getCountryChipSx } from '../../../utils/countryChips'
import { generateInspectorUrl } from '../../../utils/urls'

const isInactiveStatus = (status: string | undefined | null): boolean => {
  if (!status) return false
  const norm = status.toLowerCase()
  return norm !== 'active'
}

export interface FranchiseCellProps {
  fpCode: string | undefined | null
  fpName: string | undefined | null
  fpStatus: string | undefined | null
  /** Optional deep-link path. Defaults to `/inspect/{fp_code}`. */
  to?: string
}

export const FranchiseCell: React.FC<FranchiseCellProps> = ({
  fpCode,
  fpName,
  fpStatus,
  to,
}) => {
  const country = deriveCountryCode(fpCode)
  const inactive = isInactiveStatus(fpStatus)
  // `to` is normally supplied by the caller (built via buildRowInspectorLink);
  // the fallback only fires when a caller renders the cell without a link.
  const targetPath = to || (fpCode ? generateInspectorUrl(fpCode) : null)

  const nameLine = (
    <Typography
      component="span"
      sx={{
        fontWeight: 600,
        fontSize: '0.8125rem',
        color: 'text.primary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        display: 'block',
      }}
    >
      {fpName || fpCode || '-'}
    </Typography>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, py: 0.25, minWidth: 0 }}>
      {targetPath ? (
        <Link
          to={targetPath}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Tooltip title={fpName || fpCode || ''} enterDelay={500} disableInteractive>
            <Box sx={{ '&:hover': { textDecoration: 'underline' } }}>{nameLine}</Box>
          </Tooltip>
        </Link>
      ) : (
        nameLine
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: '11px',
          color: 'text.secondary',
          lineHeight: 1.2,
        }}
      >
        {country && (
          <Chip
            label={country}
            size="small"
            variant="outlined"
            sx={{
              ...getCountryChipSx(country),
              height: 16,
              fontSize: '0.625rem',
              fontWeight: 600,
              letterSpacing: '0.3px',
              '& .MuiChip-label': {
                px: 0.5,
                fontWeight: 600,
              },
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          component="span"
          sx={{
            fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace',
            color: 'text.secondary',
            fontSize: '11px',
          }}
        >
          {fpCode || ''}
        </Typography>
        {fpStatus && (
          <>
            <Typography
              component="span"
              sx={{ color: 'text.disabled', fontSize: '11px' }}
            >
              -
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '11px',
                fontWeight: inactive ? 600 : 500,
                color: inactive ? '#EC1C5A' : 'text.secondary',
              }}
            >
              {fpStatus}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  )
}

/** Pull the franchise tuple from a row dict, accepting either `fp_name` or
 * `franchise_name` so both exception-shape and analysis-shape rows work. */
export const extractFranchiseFields = (row: Record<string, any>) => ({
  fpCode: row.fp_code as string | undefined,
  fpName: (row.fp_name ?? row.franchise_name) as string | undefined,
  fpStatus: row.fp_status as string | undefined,
})

/** Detect whether a row has any of the franchise fields. Used by the
 * BaseReportPage auto-collapse to decide whether to swap the trio. */
export const hasFranchiseTrio = (
  columnKeys: ReadonlySet<string>,
): boolean => {
  const hasName = columnKeys.has('fp_name') || columnKeys.has('franchise_name')
  return columnKeys.has('fp_code') && hasName && columnKeys.has('fp_status')
}
